/**
 * Universal Lazy Loading Script for Images
 * 
 * Usage: Add class="lazy" to any img element you want to lazy load
 * 
 * Supported data attributes:
 * - data-src: Main image source (required)
 * - data-srcset: Responsive image srcset (optional)
 * - data-image-mobile: Alternative image for mobile devices (optional)
 * - data-srcset-mobile: Alternative srcset for mobile devices (optional)
 * 
 * Example usage:
 * <img class="lazy" 
 *      src="placeholder.jpg" 
 *      data-src="desktop-image.jpg" 
 *      data-image-mobile="mobile-image.jpg"
 *      data-srcset="desktop-400.jpg 400w, desktop-800.jpg 800w"
 *      data-srcset-mobile="mobile-400.jpg 400w, mobile-800.jpg 800w"
 *      alt="Description">
 * 
 * Features:
 * - IntersectionObserver for performance
 * - Mobile-specific image loading
 * - Responsive image support
 * - Resize handling for device switching
 * - Graceful error handling
 * - Smooth transitions via CSS classes
 */

// assets/lazy-load-images.js
document.addEventListener('DOMContentLoaded', () => {
    const lazyImages = document.querySelectorAll('img.lazy');

    if (!lazyImages || lazyImages.length === 0) return;

    // Function to determine if we're on a mobile device
    const isMobile = () => {
        return window.innerWidth <= 768; // Mobile breakpoint
    };

    // Function to load image with proper src selection
    const loadImage = (img) => {
        const mobile = isMobile();
        let srcToUse = '';
        let srcsetToUse = '';

        // Determine which src to use based on device and available data attributes
        if (mobile && img.dataset.imageMobile && img.dataset.imageMobile.trim() !== '') {
            // Use mobile-specific image if available and on mobile
            srcToUse = img.dataset.imageMobile;
            // Check for mobile-specific srcset
            srcsetToUse = img.dataset.srcsetMobile || '';
        } else if (img.dataset.src) {
            // Use regular data-src
            srcToUse = img.dataset.src;
            srcsetToUse = img.dataset.srcset || '';
        }

        // Apply the src
        if (srcToUse) {
            img.src = srcToUse;
        }

        // Apply the srcset for responsive images
        if (srcsetToUse) {
            img.srcset = srcsetToUse;
        }

        // Set loading attributes for optimization
        img.loading = 'lazy';
        img.decoding = 'async';

        // Add loaded class and remove lazy class when image loads
        img.addEventListener('load', () => {
            img.classList.add('loaded');
            img.classList.remove('lazy');
        });

        // Handle load errors gracefully
        img.addEventListener('error', () => {
            img.classList.add('error');
            img.classList.remove('lazy');
        });
    };

    if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    loadImage(img);
                    observer.unobserve(img);
                }
            });
        }, {
            rootMargin: '100px', // Start loading a bit before entering viewport
            threshold: 0.1
        });

        lazyImages.forEach(img => {
            imageObserver.observe(img);
        });

        // Re-evaluate images on window resize (for responsive mobile/desktop switching)
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Re-load images that haven't been loaded yet if device type changed
                const currentLazyImages = document.querySelectorAll('img.lazy');
                currentLazyImages.forEach(img => {
                    // Only re-evaluate if image has mobile alternative and device type might have changed
                    if (img.dataset.imageMobile) {
                        loadImage(img);
                    }
                });
            }, 250);
        });
    } else {
        // Fallback for browsers without IntersectionObserver
        lazyImages.forEach(img => {
            loadImage(img);
        });
    }
});